import * as React from 'react';
import type {
  EntityCloneItem,
  EntityDetail,
  RootEntityCloneItem,
  EnvironmentKey,
  DomainEntity,
  EntityNamesMap,
} from '@web-config-app/core';
import {
  useConfigEntity,
  useEntityDeepClone,
  useConfigAppParams,
  DeepCloneRequestBody,
  UseEntityDeepCloneOptions,
  useGetEntityTypeName,
} from '@web-config-app/core-react';
import { DeepCloneModal } from '@web-config-app/core-react-ui';
import { getDeepCloneEntityStructure } from '@web-config-app/core';
import { set, get } from 'lodash-es';
import { useIntl } from '@leagueplatform/locales';

export const DeepCloneModalContainer = ({
  isOpen,
  setIsOpen,
  deepCloneOptions,
  currentAuthoringEnvironment,
  domainEntity,
}: {
  currentAuthoringEnvironment: EnvironmentKey;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  deepCloneOptions?: UseEntityDeepCloneOptions;
  domainEntity: DomainEntity;
}) => {
  const { formatMessage } = useIntl();
  const [entitiesMap, setEntitiesMap] = React.useState<
    Record<string, EntityDetail>
  >({});
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const getEntityTypeName = useGetEntityTypeName();
  const configEntity = useConfigEntity({ domainEntity });
  const { entityInstanceId } = useConfigAppParams();

  /**
   * Extract entity names from entitiesMap
   * This is helpful to handle duplicate entities in the relationship structure because:
   * 1. Only the first instance of the entity name should be editable
   * 2. All other instances inputs must be readOnly (controlled via the isDuplicate flag)
   * 3. All instances must display the same updated name (controlled via this entityNames prop)
   */
  const entityNamesMap = React.useMemo(() => {
    const names: EntityNamesMap = {};
    Object.entries(entitiesMap).forEach(([id, entity]) => {
      const name = get(entity, 'attributes.entityMetadata.name');
      names[id] = name;
    });
    return names;
  }, [entitiesMap]);

  const {
    deepCloneEntity,
    deepCloneGetData,
    isGetFetching: isLoadingDeepCloneGetData,
    fetchErrorMessage: deepCloneFetchErrorMessage,
    isGetError: isDeepCloneGetError,
  } = useEntityDeepClone({
    entity: configEntity,
    instanceId: isOpen ? entityInstanceId : undefined,
    environment: currentAuthoringEnvironment,
    options: deepCloneOptions,
  });

  /**
   * Replaces the domainEntityType field in the structured entity with the
   * corresponding translated name, including its childEntities.
   */
  const replaceEntityTypeWithName = React.useCallback(
    (structured: RootEntityCloneItem) => {
      const replace = (entity: RootEntityCloneItem | EntityCloneItem) => {
        const count = entity?.type === 'root' ? 1 : 2;
        const updatedEntity = {
          ...entity,
          domainEntityType: entity.domainEntityType
            ? getEntityTypeName(entity.domainEntityType, count)
            : entity.domainEntityType,
        };

        if (updatedEntity.childEntities) {
          updatedEntity.childEntities =
            updatedEntity.childEntities.map(replace);
        }

        return updatedEntity;
      };

      return replace(structured);
    },
    [getEntityTypeName],
  );

  const data = React.useMemo(() => {
    if (deepCloneGetData?.data) {
      try {
        const { structured } = getDeepCloneEntityStructure(deepCloneGetData);
        return replaceEntityTypeWithName(structured) as RootEntityCloneItem;
      } catch (e: any) {
        setErrorMessage(e.toString());
      }
    }
    return null;
  }, [deepCloneGetData, replaceEntityTypeWithName]);

  React.useEffect(() => {
    if (deepCloneGetData?.data) {
      try {
        const { map } = getDeepCloneEntityStructure(deepCloneGetData);
        setEntitiesMap(map);
      } catch (e) {
        console.error(e);
      }
    }
  }, [deepCloneGetData]);

  const deepCloneAction = () => {
    if (deepCloneGetData) {
      deepCloneEntity({
        data: entitiesMap[deepCloneGetData.data.id] ?? deepCloneGetData.data,
        included:
          deepCloneGetData.included?.map((includedEntity: EntityDetail) => {
            const entity = entitiesMap[includedEntity.id];
            return entity ?? includedEntity;
          }) ?? [],
      } as DeepCloneRequestBody);
    }
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (isDeepCloneGetError) {
      const serverErrorMessage =
        deepCloneFetchErrorMessage?.message ??
        formatMessage({ id: 'DEEP_CLONE_MODAL_FETCH_NO_MESSAGE_PROVIDED' });
      const deepCloneGetErrorMessage = formatMessage(
        { id: 'DEEP_CLONE_MODAL_FETCH_ERROR_MESSAGE' },
        { serverErrorMessage },
      );
      setErrorMessage(deepCloneGetErrorMessage);
    } else {
      setErrorMessage(null);
    }
  }, [isDeepCloneGetError, deepCloneFetchErrorMessage, formatMessage]);

  const onNameChange = (id: string, name: string) => {
    const updateEntity = { ...entitiesMap[id] };
    if (updateEntity) {
      set(updateEntity, 'attributes.entityMetadata.name', name);
      setEntitiesMap({
        ...entitiesMap,
        [id]: updateEntity,
      });
    }
  };

  return (
    <DeepCloneModal
      onClose={() => {
        setIsOpen(false);
        setErrorMessage(null);
      }}
      isOpen={isOpen}
      data={data}
      errorMessage={errorMessage}
      deepCloneAction={deepCloneAction}
      onNameChange={onNameChange}
      isLoadingDeepCloneGetData={isLoadingDeepCloneGetData}
      entityNamesMap={entityNamesMap}
    />
  );
};
